import { OAuth2Service } from '@maximizer/oauth2/data-access';
import {
  GlobalServicesLoginDetails,
} from '@maximizer/core/shared/domain';
import { OnPremLoginDetails } from '@maximizer/core/shared/data-access';
import { secureRandomId } from './secure-random-id';

export const SYNC_CONFIG_HANDSHAKE_REQUEST = 'mx-sync-config-handshake-request';
export const SYNC_CONFIG_HANDSHAKE_RESPONSE =
  'mx-sync-config-handshake-response';
export const SYNC_CONFIG_HANDSHAKE_COMPLETE =
  'mx-sync-config-handshake-complete';

/**
 * Query-param name the opener uses to pass a one-time handshake nonce to the
 * popup. The popup echoes it back inside the handshake request; the opener
 * refuses to respond unless the nonces match. This binds the handshake to the
 * specific popup the opener launched and stops any other same-origin window
 * from replaying the request to grab the bearer token.
 */
export const SYNC_CONFIG_HANDSHAKE_NONCE_PARAM = 'hs';

/**
 * Query-param the opener uses to pass its active UI language to the popup,
 * which runs outside the Office host and has no `Office.context.displayLanguage`.
 */
export const SYNC_CONFIG_LANG_PARAM = 'lang';

/**
 * Session key that keeps the requested language across the OAuth redirect,
 * which returns to `/sync-configuration` without the query param.
 */
export const SYNC_CONFIG_LANG_STORAGE_KEY = 'mx-sync-config-lang';

/**
 * Generates a fresh handshake nonce for a popup. The nonce gates bearer-token
 * release, so it must be cryptographically strong.
 */
export function generateHandshakeNonce(): string {
  return secureRandomId();
}

const SYNC_CONFIG_HANDSHAKE_TIMEOUT_MS = 5000;
const SYNC_CONFIG_HANDSHAKE_CHILD_POLL_MS = 2000;

interface SyncConfigHandshakeRequest {
  type: typeof SYNC_CONFIG_HANDSHAKE_REQUEST;
  nonce: string;
}

interface SyncConfigHandshakeResponse {
  type: typeof SYNC_CONFIG_HANDSHAKE_RESPONSE;
  payload: SyncConfigHandshakePayload;
}

interface SyncConfigHandshakeComplete {
  type: typeof SYNC_CONFIG_HANDSHAKE_COMPLETE;
}

export interface SyncConfigHandshakePayload {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  expiresAt: Date | string;
  isCloud: boolean;
  cloudLoginDetails?: GlobalServicesLoginDetails;
  onPremLoginDetails?: OnPremLoginDetails;
  mxUserId?: string;
  mxUserEmail?: string;
}

interface OpenerDeps {
  oAuthService: OAuth2Service;
  isCloud: boolean | undefined;
  /**
   * Per-popup handshake nonce. Must be passed through the popup URL via the
   * `SYNC_CONFIG_HANDSHAKE_NONCE_PARAM` query parameter so the popup can echo
   * it back. The opener will refuse to respond if the nonces do not match.
   */
  nonce: string;
}

export function respondToHandshakeFromOpener(
  childWindow: Window,
  deps: OpenerDeps,
): { dispose(): void } {
  const expectedOrigin = window.location.origin;
  let disposed = false;

  const listener = (event: MessageEvent): void => {
    if (disposed) return;
    if (event.origin !== expectedOrigin) return;
    if (event.source !== childWindow) return;

    const data = event.data as
      | SyncConfigHandshakeRequest
      | SyncConfigHandshakeComplete
      | undefined;
    if (!data?.type) return;

    if (data.type === SYNC_CONFIG_HANDSHAKE_COMPLETE) {
      cleanup();
      return;
    }

    if (data.type !== SYNC_CONFIG_HANDSHAKE_REQUEST) return;
    if (!deps.nonce || data.nonce !== deps.nonce) return;

    void (async () => {
      try {
        await deps.oAuthService.getStorageToken();
      } catch {
        return;
      }

      if (disposed) return;

      const payload = buildHandshakePayload(deps.oAuthService, deps.isCloud);
      if (!payload) {
        cleanup();
        return;
      }

      const response: SyncConfigHandshakeResponse = {
        type: SYNC_CONFIG_HANDSHAKE_RESPONSE,
        payload,
      };
      childWindow.postMessage(response, expectedOrigin);
    })();
  };

  const closedPoll = window.setInterval(() => {
    if (childWindow.closed) cleanup();
  }, SYNC_CONFIG_HANDSHAKE_CHILD_POLL_MS);
  window.addEventListener('message', listener);

  function cleanup(): void {
    if (disposed) return;
    disposed = true;
    window.removeEventListener('message', listener);
    window.clearInterval(closedPoll);
  }

  return { dispose: cleanup };
}

function buildHandshakePayload(
  oAuthService: OAuth2Service,
  isCloudHint?: boolean,
): SyncConfigHandshakePayload | null {
  const oAuth2Token = oAuthService.oAuth2Token;
  if (!oAuth2Token?.access_token) return null;

  const isCloud = isCloudHint ?? !!oAuthService.cloudLoginDetails;
  const cloudLoginDetails: GlobalServicesLoginDetails | undefined =
    oAuthService.cloudLoginDetails;
  const onPremLoginDetails: OnPremLoginDetails | undefined =
    oAuthService.onPremLoginDetails;
  const mxUserId =
    oAuthService.mxUserId ?? oAuthService.getStorageMxUserId() ?? undefined;
  const mxUserEmail =
    oAuthService.mxUserEmail ??
    oAuthService.getStorageMxUserEmail() ??
    undefined;

  return {
    accessToken: oAuth2Token.access_token,
    tokenType: oAuth2Token.token_type,
    expiresIn: oAuth2Token.expires_in,
    expiresAt: oAuth2Token.expires_at,
    isCloud,
    cloudLoginDetails,
    onPremLoginDetails,
    mxUserId,
    mxUserEmail,
  };
}

export async function requestHandshakeFromOpener(
  oAuthService: OAuth2Service,
  opts?: { nonce?: string },
): Promise<boolean> {
  const opener = window.opener as Window | null;
  if (!opener) return false;
  if (!isOpenerSameOrigin(opener)) return false;

  const nonce =
    opts?.nonce ??
    new URLSearchParams(window.location.search).get(
      SYNC_CONFIG_HANDSHAKE_NONCE_PARAM,
    );
  if (!nonce) return false;

  const expectedOrigin = window.location.origin;
  const payload = await new Promise<SyncConfigHandshakePayload | null>(
    (resolve) => {
      let settled = false;

      const listener = (event: MessageEvent): void => {
        if (settled) return;
        if (event.origin !== expectedOrigin) return;
        if (event.source !== opener) return;
        const data = event.data as SyncConfigHandshakeResponse | undefined;
        if (data?.type !== SYNC_CONFIG_HANDSHAKE_RESPONSE) return;

        settled = true;
        cleanup();
        resolve(data.payload ?? null);
      };

      const timeoutId = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(null);
      }, SYNC_CONFIG_HANDSHAKE_TIMEOUT_MS);

      function cleanup(): void {
        window.removeEventListener('message', listener);
        window.clearTimeout(timeoutId);
      }

      window.addEventListener('message', listener);
      const request: SyncConfigHandshakeRequest = {
        type: SYNC_CONFIG_HANDSHAKE_REQUEST,
        nonce,
      };
      opener.postMessage(request, expectedOrigin);
    },
  );

  if (!payload) return false;

  // No COMPLETE is sent here: the responder must stay armed so the popup can
  // re-handshake after a full-page OAuth redirect (install and re-auth flows).
  // The opener cleans up via its closed-poll or an explicit terminal COMPLETE.
  return oAuthService.hydrateBrowserOnlyAccessToken(payload);
}

function isOpenerSameOrigin(opener: Window): boolean {
  try {
    return opener.location.origin === window.location.origin;
  } catch {
    return false;
  }
}
